import { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowDownRight, 
  ArrowUpRight, 
  Users, 
  TrendingUp, 
  TrendingDown,
  Loader2,
  Search,
  Calendar,
  UserPlus,
  UserMinus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMemberMovementsPage() {
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchMovements();
  }, [year, dateFrom, dateTo, typeFilter]);

  const fetchMovements = async () => {
    setLoading(true);
    const token = getToken();
    try {
      let url = `${API_URL}/api/admin/member-movements?year=${year}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      if (typeFilter) url += `&type=${typeFilter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovements(response.data.movements || []);
      setStats(response.data.stats || null);
    } catch (error) {
      toast.error('Fehler beim Laden der Mitgliederbewegungen');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...movements];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.note?.toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [movements, searchQuery, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="opacity-50" />;
    return sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const handleYearChange = (newYear) => {
    setYear(newYear);
    setDateFrom('');
    setDateTo('');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Users className="text-primary" size={28} />
          <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
            Mitgliederbewegungen
          </h1>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-500/10 border border-green-500/30 p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <UserPlus size={18} className="text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-testid="stats-entries">{stats.entries}</p>
              <p className="text-xs text-muted-foreground uppercase">Eintritte</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <UserMinus size={18} className="text-red-600" />
              </div>
              <p className="text-2xl font-bold text-red-600" data-testid="stats-exits">{stats.exits}</p>
              <p className="text-xs text-muted-foreground uppercase">Austritte</p>
            </div>
            <div className={`border p-4 text-center ${stats.net >= 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
              <div className="flex items-center justify-center gap-2 mb-1">
                {stats.net >= 0 ? <TrendingUp size={18} className="text-blue-600" /> : <TrendingDown size={18} className="text-amber-600" />}
              </div>
              <p className={`text-2xl font-bold ${stats.net >= 0 ? 'text-blue-600' : 'text-amber-600'}`} data-testid="stats-net">
                {stats.net > 0 ? '+' : ''}{stats.net}
              </p>
              <p className="text-xs text-muted-foreground uppercase">Netto</p>
            </div>
            <div className="bg-card border border-border p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users size={18} className="text-foreground" />
              </div>
              <p className="text-2xl font-bold text-foreground" data-testid="stats-active">{stats.active_total}</p>
              <p className="text-xs text-muted-foreground uppercase">Aktive Mitglieder</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Suche..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-48 md:w-64 bg-secondary/50 border-border rounded-none"
              data-testid="movements-search"
            />
          </div>

          <select
            value={year}
            onChange={(e) => handleYearChange(parseInt(e.target.value))}
            className="h-10 px-3 bg-secondary/50 border border-border rounded-none text-foreground"
            data-testid="year-filter"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 px-3 bg-secondary/50 border border-border rounded-none text-foreground"
            data-testid="type-filter"
          >
            <option value="">Alle Bewegungen</option>
            <option value="entry">Eintritte</option>
            <option value="exit">Austritte</option>
          </select>

          <div className="flex items-center gap-1">
            <Calendar size={16} className="text-muted-foreground flex-shrink-0" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-10 w-36 bg-secondary/50 border-border rounded-none text-sm"
              placeholder="Von"
              data-testid="date-from"
            />
            <span className="text-muted-foreground text-sm">–</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-10 w-36 bg-secondary/50 border-border rounded-none text-sm"
              placeholder="Bis"
              data-testid="date-to"
            />
          </div>

          <Button
            variant="outline"
            onClick={fetchMovements}
            className="rounded-none"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Movements Table */}
        <div className="bg-card border border-border">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredAndSorted.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>Keine Mitgliederbewegungen gefunden</p>
              <p className="text-sm mt-2">
                {searchQuery ? `Keine Ergebnisse für "${searchQuery}"` : `Keine Eintritte oder Austritte im gewählten Zeitraum`}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full" data-testid="movements-table">
                  <thead className="bg-muted/30">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('type')}
                      >
                        <span className="flex items-center gap-1">Typ <SortIcon field="type" /></span>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('name')}
                      >
                        <span className="flex items-center gap-1">Mitglied <SortIcon field="name" /></span>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                        onClick={() => handleSort('date')}
                      >
                        <span className="flex items-center gap-1">Datum <SortIcon field="date" /></span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Notiz
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Rechnungen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredAndSorted.map((m, idx) => (
                      <tr key={`${m.member_id}-${m.type}-${idx}`} className="hover:bg-muted/20">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {m.type === 'entry' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-600" data-testid={`type-badge-${idx}`}>
                              <ArrowUpRight size={12} />
                              Eintritt
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-600" data-testid={`type-badge-${idx}`}>
                              <ArrowDownRight size={12} />
                              Austritt
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Link to={`/members/${m.member_id}`} className="hover:text-primary transition-colors">
                            <div className="text-sm font-medium text-foreground">{m.name}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          </Link>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDate(m.date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                          {m.note || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {m.cancelled_invoices && m.cancelled_invoices.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {m.cancelled_invoices.map(inv => (
                                <Link
                                  key={inv.invoice_number}
                                  to={`/admin/invoices`}
                                  className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-500 transition-colors"
                                >
                                  <FileText size={12} />
                                  {inv.invoice_number} ({inv.amount?.toFixed(2)} CHF)
                                </Link>
                              ))}
                            </div>
                          ) : m.type === 'exit' ? (
                            <span className="text-xs text-muted-foreground">Keine</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-border">
                {filteredAndSorted.map((m, idx) => (
                  <div key={`${m.member_id}-${m.type}-${idx}`} className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <Link to={`/members/${m.member_id}`} className="hover:text-primary transition-colors">
                        <p className="font-medium text-foreground">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.email}</p>
                      </Link>
                      {m.type === 'entry' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-500/20 text-green-600 rounded-full">
                          <ArrowUpRight size={12} /> Eintritt
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/20 text-red-600 rounded-full">
                          <ArrowDownRight size={12} /> Austritt
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{formatDate(m.date)}</span>
                      {m.note && <span className="text-xs text-muted-foreground truncate max-w-[50%]">{m.note}</span>}
                    </div>
                    {m.cancelled_invoices && m.cancelled_invoices.length > 0 && (
                      <div className="pt-1 border-t border-border/50">
                        {m.cancelled_invoices.map(inv => (
                          <span key={inv.invoice_number} className="text-xs text-amber-600">
                            <FileText size={10} className="inline mr-1" />
                            {inv.invoice_number} ({inv.amount?.toFixed(2)} CHF)
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Summary */}
        {!loading && filteredAndSorted.length > 0 && (
          <p className="text-xs text-muted-foreground text-right">
            {filteredAndSorted.length} Bewegung{filteredAndSorted.length !== 1 ? 'en' : ''} angezeigt
          </p>
        )}
      </div>
    </Layout>
  );
}
